import { parseGameMessage, type GameToShellMessage } from '@studio/shared';

/**
 * State the shell keeps about the game running in the sandboxed frame.
 *
 * Every field here is DISPLAY ONLY. A message from a game can change what the
 * child sees on the page around it and nothing else — it can never affect
 * credits, the jam clock, publishing, or any server-side state. That is the
 * whole reason this is a pure reducer over a validated message: the sandbox has
 * an opaque origin, so `event.origin` tells us nothing, and the message shape is
 * the only thing we can check.
 */
export type GameState = {
  status: 'loading' | 'playing' | 'won' | 'lost' | 'broken';
  score: number;
  error: { message: string; stack?: string } | null;
  /** Messages that failed validation. Surfaced to the parent console, not the child. */
  dropped: number;
};

export const initialGameState: GameState = {
  status: 'loading',
  score: 0,
  error: null,
  dropped: 0,
};

/**
 * A game that throws within this window of loading is treated as broken on
 * arrival and offered a free repair. Later errors are the child's own edits or
 * ordinary play, and are shown without offering to spend anything.
 */
export const BROKEN_ON_ARRIVAL_MS = 3000;

export type GameEvent = { data: unknown; msSinceLoad: number };

export function reduceGameEvent(state: GameState, event: GameEvent): GameState {
  const message: GameToShellMessage | null = parseGameMessage(event.data);
  if (!message) return { ...state, dropped: state.dropped + 1 };

  switch (message.type) {
    case 'ready':
      // A game that already failed does not become healthy by saying hello.
      return state.status === 'broken' ? state : { ...state, status: 'playing' };

    case 'score':
      return { ...state, score: message.value };

    case 'win':
      return { ...state, status: 'won' };

    case 'lose':
      return { ...state, status: 'lost' };

    case 'request-restart':
      return { ...initialGameState, dropped: state.dropped };

    case 'error':
      return {
        ...state,
        error: state.error ?? { message: message.message, ...(message.stack ? { stack: message.stack } : {}) },
        status:
          event.msSinceLoad <= BROKEN_ON_ARRIVAL_MS ? 'broken' : state.status,
      };
  }
}

/** Whether to offer the one free repair. */
export const canOfferRepair = (state: GameState): boolean =>
  state.status === 'broken' && state.error !== null;
